/**
 * Mobile bottom tab bar — fixed dock at the bottom of the screen on
 * phones (hidden on desktop md+). live sale platforms-style: 5 anchor destinations
 * always one tap away. Pinned ABOVE the page footer even when scrolled
 * to the very bottom — exactly like live sale platforms/Instagram/TikTok.
 *
 *   Live   → /live (the live-now grid)
 *   Shop   → /browse (full marketplace)
 *   Inbox  → /messages
 *   Me     → /settings/profile (or /apply when not a verified seller)
 *   Sell   → /seller-hub (verified sellers) | /apply (buyers)
 *
 * Hidden inside any live stream page (/stream/:id) so we don't fight
 * with the stream's own bottom controls.
 */
import { Link, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import {
  BroadcastIcon,
  StorefrontIcon,
  ChatCircleDotsIcon,
  UserCircleIcon,
  StarFourIcon,
} from "@phosphor-icons/react";

function Tab({ to, label, icon: Icon, active, testid, badge }) {
  return (
    <Link
      to={to}
      data-testid={testid}
      className={`relative flex-1 flex flex-col items-center justify-center gap-0.5 py-1.5 active:scale-95 transition ${
        active ? "text-[#1D78AB]" : "text-zinc-500"
      }`}
    >
      <Icon size={22} weight={active ? "fill" : "regular"} />
      <span className="text-[10px] font-bold leading-none">{label}</span>
      {badge != null && badge > 0 && (
        <span className="absolute top-0.5 right-1/2 translate-x-3 min-w-[16px] h-4 px-1 rounded-full bg-rose-500 text-white text-[9px] font-black grid place-items-center">
          {badge > 9 ? "9+" : badge}
        </span>
      )}
    </Link>
  );
}

export default function MobileTabBar({ unreadMessages = 0 }) {
  const { user } = useAuth();
  const { pathname } = useLocation();

  // Don't render on live stream pages — their own bottom rail takes over.
  if (pathname.startsWith("/stream/")) return null;
  // Hide on auth/onboarding pages — login/register/apply/etc. feel
  // cleaner full-bleed without a bottom dock.
  if (/^\/(login|register|apply|wholesale-credentials|auth)/.test(pathname)) return null;

  const isSeller = user && (user.account_type || "").includes("seller");
  const profilePath = user ? "/settings/profile" : "/login";

  const isActive = (prefix, exact = false) =>
    exact ? pathname === prefix : pathname === prefix || pathname.startsWith(prefix + "/");

  return (
    <nav
      className="md:hidden fixed inset-x-0 bottom-0 z-40 bg-white/95 backdrop-blur border-t border-zinc-200 flex"
      style={{
        // Use the REAL Samsung nav height measured natively by
        // MainActivity.java and injected as `--sai-bottom` on every
        // page load. `env(safe-area-inset-bottom)` is broken on
        // Samsung One UI WebViews — it reports 0 on some pages and
        // 200+px on others. The native injection always reflects the
        // actual current OS chrome height. The 16px fallback only
        // applies on the web (no MainActivity injecting the var).
        paddingBottom: "var(--sai-bottom, 16px)",
      }}
      data-testid="mobile-tab-bar"
    >
      <Tab
        to="/live"
        label="Live"
        icon={BroadcastIcon}
        active={isActive("/live")}
        testid="mobile-tab-live"
      />
      <Tab
        to="/browse"
        label="Shop"
        icon={StorefrontIcon}
        active={isActive("/browse") || isActive("/categories") || pathname === "/"}
        testid="mobile-tab-shop"
      />
      <Tab
        to="/messages"
        label="Inbox"
        icon={ChatCircleDotsIcon}
        active={isActive("/messages")}
        testid="mobile-tab-messages"
        badge={unreadMessages}
      />
      <Tab
        to={profilePath}
        label="Me"
        icon={UserCircleIcon}
        active={isActive("/settings/profile") || isActive("/me")}
        testid="mobile-tab-profile"
      />
      {/* Hub — only shown to approved seller accounts (retail_seller /
          wholesale_seller). Buyers and logged-out visitors don't see it;
          they apply to sell via the dedicated /apply CTA elsewhere. */}
      {isSeller && (
        <Tab
          to="/seller-hub"
          label="Hub"
          icon={StarFourIcon}
          active={isActive("/seller-hub") || isActive("/sell")}
          testid="mobile-tab-seller-hub"
        />
      )}
    </nav>
  );
}
